import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { BusinessType } from './BusinessTypeSelector'

interface SelectionTransitionProps {
  selectedType: BusinessType
  onComplete: () => void
}

const SelectionTransition: React.FC<SelectionTransitionProps> = ({ 
  selectedType, 
  onComplete, 
}) => {
  const [show, setShow] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(false)
      setTimeout(onComplete, 400)
    }, 1800)

    return () => clearTimeout(timer)
  }, [onComplete])

  const getColors = () => {
    switch (selectedType) {
      case 'sales': return { primary: '#06b6d4', secondary: '#3b82f6' }
      case 'school': return { primary: '#f59e0b', secondary: '#f97316' }
      case 'transport': return { primary: '#10b981', secondary: '#14b8a6' }
      default: return { primary: '#06b6d4', secondary: '#3b82f6' }
    }
  }

  const colors = getColors()

  // Generate multiple sparkle layers
  const sparkles = Array.from({ length: 24 }, (_, i) => {
    const angle = (i / 24) * Math.PI * 2
    const radius = 100 + Math.random() * 50
    return {
      id: i,
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      delay: i * 0.025,
      size: 6 + Math.random() * 10,
      duration: 1.2 + Math.random() * 0.6,
    }
  })

  // Additional floating sparkles
  const floatingSparkles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: (Math.random() - 0.5) * 400,
    y: (Math.random() - 0.5) * 400,
    delay: 0.3 + i * 0.04,
    size: 8 + Math.random() * 12,
  }))

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] overflow-hidden"
        >
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

          <div className="absolute inset-0 flex items-center justify-center">
            {/* Radiating sparkles */}
            {sparkles.map((sparkle) => (
              <motion.div
                key={sparkle.id}
                initial={{ 
                  scale: 0, 
                  opacity: 0,
                  x: 0,
                  y: 0,
                  rotate: 0,
                }}
                animate={{ 
                  scale: [0, 1.2, 0],
                  opacity: [0, 1, 0],
                  x: sparkle.x,
                  y: sparkle.y,
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: sparkle.duration,
                  delay: sparkle.delay,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
                className="absolute"
                style={{
                  width: sparkle.size,
                  height: sparkle.size,
                }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="text-cyan-400 drop-shadow-lg">
                  <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
                </svg>
              </motion.div>
            ))}

            {/* Floating sparkles with different animation */}
            {floatingSparkles.map((sparkle) => (
              <motion.div
                key={`float-${sparkle.id}`}
                initial={{ 
                  scale: 0, 
                  opacity: 0,
                  x: 0,
                  y: 0,
                }}
                animate={{ 
                  scale: [0, 1, 1, 0],
                  opacity: [0, 1, 0.8, 0],
                  x: [0, sparkle.x * 0.3, sparkle.x],
                  y: [0, sparkle.y * 0.3, sparkle.y],
                  rotate: [0, 90, 180],
                }}
                transition={{
                  duration: 1.6,
                  delay: sparkle.delay,
                  ease: 'easeOut',
                }}
                className="absolute"
                style={{
                  width: sparkle.size,
                  height: sparkle.size,
                }}
              >
                <div 
                  className="w-full h-full rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${colors.secondary} 0%, transparent 70%)`,
                    boxShadow: `0 0 ${sparkle.size * 2}px ${colors.secondary}`,
                  }}
                />
              </motion.div>
            ))}

            {/* Central logo with elegant entrance */}
            <motion.div
              initial={{ scale: 0, opacity: 0, rotate: -45 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{
                type: 'spring',
                stiffness: 160,
                damping: 20,
                delay: 0.15,
              }}
              className="relative z-10"
            >
              <motion.div
                animate={{
                  boxShadow: [
                    `0 0 40px ${colors.primary}50, 0 0 80px ${colors.primary}30`,
                    `0 0 80px ${colors.primary}70, 0 0 120px ${colors.primary}40`,
                    `0 0 40px ${colors.primary}50, 0 0 80px ${colors.primary}30`,
                  ],
                }}
                transition={{ duration: 1, repeat: 1 }}
                className="w-44 h-44 rounded-3xl overflow-hidden bg-white/5 backdrop-blur-2xl p-8 border-2 border-white/20"
                style={{
                  boxShadow: `0 0 60px ${colors.primary}40`,
                }}
              >
                <motion.img 
                  src="/logo.svg" 
                  alt="VarkWave" 
                  className="w-full h-full object-contain"
                  animate={{ 
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: 1,
                    ease: 'easeInOut',
                  }}
                />
              </motion.div>

              {/* Pulsing orbital rings */}
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-3xl border-2"
                  style={{ borderColor: `${colors.primary}60` }}
                  initial={{ scale: 1, opacity: 0 }}
                  animate={{ 
                    scale: [1, 1.3 + i * 0.2, 1.8 + i * 0.3],
                    opacity: [0, 0.7, 0],
                  }}
                  transition={{
                    duration: 1.5,
                    delay: i * 0.15,
                    ease: 'easeOut',
                  }}
                />
              ))}
            </motion.div>

            {/* Rotating gradient background ring */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5, rotate: 0 }}
              animate={{ 
                opacity: [0, 0.4, 0],
                scale: [0.5, 1.5, 2],
                rotate: 360,
              }}
              transition={{
                duration: 1.8,
                ease: 'easeOut',
              }}
              className="absolute w-96 h-96 rounded-full"
              style={{
                background: `conic-gradient(from 0deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`,
                filter: 'blur(60px)',
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SelectionTransition
